import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Check, Globe, User, PanelLeftClose, PanelLeft } from 'lucide-react'
import { LoginDialog } from '../features/auth/LoginDialog'
import { LOGIN_INTENT_EVENT, SIGNUP_INTENT_EVENT, type LoginIntent } from '../../lib/loginIntent'
import { SignupDialog } from '../features/auth/SignupDialog'
import { UserMenu } from '../features/auth/UserMenu'
import { useCommunity } from '@/lib/CommunityContext'
import { LanguageSelector } from './LanguageSelector'
import { useAuth } from '@/lib/AuthContext'
import { useSidebar } from '@/lib/SidebarContext'
import { useTranslation } from 'react-i18next'
import { AppLink } from '@/components/ui/app-link'
import { NotificationBell } from '@/components/features/notifications/NotificationBell'
import { ReportProblemButton } from '@/components/shared/ReportProblem'
import { imageUrl, imageSrcSet } from '@/lib/images'
import { useCommunityLogo } from '@/lib/useDarkTheme'
import { cn } from '@/lib/utils'
import { useCommunityNavigation } from '@/lib/navigation'
import { TopNavBar } from './TopNav'

export const Header = () => {
  const { t } = useTranslation(['auth'])
  const { t: tNav } = useTranslation('navigation')
  const { toggle, isOpen, toggleCollapsed, isCollapsed, isDesktop, mode, setMemberMode, isTopNav, topNavConfigured, menuEditing, setMenuEditing } = useSidebar()
  /* F-191 phase 5: the native elements the community hides for everyone (§4.5.1). */
  const { hiddenNative } = useCommunityNavigation()
  const showLanguage = !hiddenNative.includes('languageSelector')
  const showReport = !hiddenNative.includes('reportProblem')
  const { community } = useCommunity()
  const logo = useCommunityLogo()
  const { isAuthenticated, isValidating, sessionNotice, clearSessionNotice } = useAuth()
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const [loginIntent, setLoginIntent] = useState<LoginIntent | null>(null)

  // Pages open the login with an e-mail or a notice through this event (PROF-3).
  React.useEffect(() => {
    const onIntent = (event: Event) => {
      setLoginIntent((event as CustomEvent<LoginIntent>).detail || {})
      setLoginDialogOpen(true)
    }
    window.addEventListener(LOGIN_INTENT_EVENT, onIntent)
    return () => window.removeEventListener(LOGIN_INTENT_EVENT, onIntent)
  }, [])
  const [signupDialogOpen, setSignupDialogOpen] = useState(false)
  /* F-161a: the offer of a padlocked area asks for the signup (the login when signup is off). */
  React.useEffect(() => {
    const onSignup = () => {
      if (community?.signupEnabled) setSignupDialogOpen(true)
      else setLoginDialogOpen(true)
    }
    window.addEventListener(SIGNUP_INTENT_EVENT, onSignup)
    return () => window.removeEventListener(SIGNUP_INTENT_EVENT, onSignup)
  }, [community?.signupEnabled])

  /* F-119: the directory (hub.memberfy.net) links to `/?signup=1` and `/?login=1`.
     Once the community and the session are known, the flag opens the right
     dialog (signup falls back to login when the community has signup off) and
     leaves the address bar, so a reload does not open it again. Signed in,
     the flag is only dropped. */
  React.useEffect(() => {
    if (!community || isValidating) return
    const params = new URLSearchParams(window.location.search)
    const wantsSignup = params.get('signup') === '1'
    const wantsLogin = params.get('login') === '1'
    if (!wantsSignup && !wantsLogin) return
    params.delete('signup')
    params.delete('login')
    const query = params.toString()
    window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash)
    if (isAuthenticated) return
    if (wantsSignup && community.signupEnabled) setSignupDialogOpen(true)
    else setLoginDialogOpen(true)
  }, [community, isValidating, isAuthenticated])

  /* The session ended under the person (F-31 F3 401): they are simply signed out, with no message. */
  React.useEffect(() => {
    if (sessionNotice) clearSessionNotice()
  }, [sessionNotice, clearSessionNotice])

  /* F-191 phase 2: on desktop the button switches full <-> compact (the
     member's choice, kept per community); closing the menu for good is the
     phone's drawer. A page that hides the menu (pricing, checkout) gets it back. */
  const handleToggleSidebar = () => {
    if (!isDesktop) toggle()
    else if (isCollapsed) toggleCollapsed()
    else setMemberMode(mode === 'rail' ? 'full' : 'rail')
  }

  const isSidebarWide = isDesktop ? !isCollapsed && mode === 'full' : isOpen
  const toggleLabel = isDesktop
    ? tNav(isCollapsed ? 'sidebar.showMenu' : mode === 'rail' ? 'sidebar.expandMenu' : 'sidebar.compactMenu')
    : tNav(isOpen ? 'sidebar.closeMenu' : 'sidebar.showMenu')

  const logoLink = (
    <AppLink
      to="/"
      className="flex items-center gap-2 hover:opacity-80 transition-opacity"
    >
      {logo.url && logo.url !== 'https://via.placeholder.com/150' ? (
        <img
          src={imageUrl(logo.url, { w: 32, h: 32, fit: 'max' }) || undefined} srcSet={imageSrcSet(logo.url, { w: 32, h: 32, fit: 'max' })}
          alt={community?.name}
          className={cn('h-8 w-8 object-contain rounded-md', logo.plate && 'theme-light bg-surface p-0.5')}
        />
      ) : (
        <Globe className="h-6 w-6 text-primary" />
      )}
      {/* Signed in, the phone header carries chat, people and the bell: the logo alone keeps them from overlapping. */}
      <span className={cn(
        isTopNav ? 'max-w-[96px] sm:max-w-[200px]' : isAuthenticated ? 'max-w-[96px] sm:max-w-[240px] lg:max-w-none' : 'max-w-[120px] sm:max-w-none',
        'truncate text-lg font-semibold sm:text-xl'
      )}>{community?.name}</span>
    </AppLink>
  )
  const rightCluster = (
    <>
      {/* F-191 phase 5: editing the menu brought the side menu back; this returns to the bar on top. */}
      {topNavConfigured && menuEditing && (
        <Button size="sm" variant="primary" onClick={() => setMenuEditing(false)} data-top-edit-done>
          <Check className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{tNav('topNav.doneEditing')}</span>
        </Button>
      )}
      {showLanguage && <LanguageSelector />}
      {/* With the bar on top there is no drawer: signing in and up sit in the header on phones too. */}
      {isTopNav && !isAuthenticated && (
        <div className="flex items-center gap-1.5 lg:hidden">
          <Button size="sm" variant="outline" onClick={() => setLoginDialogOpen(true)} data-top-login>{t('signIn', { ns: 'auth' })}</Button>
          {community?.signupEnabled && (
            <Button size="sm" variant="primary" className="hidden min-[400px]:inline-flex" onClick={() => setSignupDialogOpen(true)}>
              {community.signupRequiresApproval ? t('requestAccess', { ns: 'auth' }) : t('createAccount', { ns: 'auth' })}
            </Button>
          )}
        </div>
      )}
      {!isAuthenticated && (
        <div className="hidden lg:flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setLoginDialogOpen(true)}
            className="bg-white/60 backdrop-blur-sm border-white/50 hover:bg-white/80 text-gray-600"
            data-login-trigger
          >
            <User className="h-4 w-4 mr-2" />
            {t('signIn', { ns: 'auth' })}
          </Button>
          {community?.signupEnabled && (
            <Button
              onClick={() => setSignupDialogOpen(true)}
              variant="primary"
              data-signup-trigger
            >
              {community.signupRequiresApproval ? t('requestAccess', { ns: 'auth' }) : t('createAccount', { ns: 'auth' })}
            </Button>
          )}
        </div>
      )}
      {/* Reportar problema (David, 2026-10-29: out of the sidebar), the bell, then the name and avatar:
          F-184, the avatar opens "me" and the chat (no separate chat icon). */}
      {isAuthenticated && showReport && <ReportProblemButton />}
      {isAuthenticated && <NotificationBell />}
      {isAuthenticated && <UserMenu />}
    </>
  )

  return (
    <header className="theme-region-header fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-xl border-b border-white/20 z-50 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      {isTopNav ? (
        /* F-191 phase 5: the bar on top. The logo on the left, the sections (from 1024 px) and the right cluster; no menu button. */
        <div className="flex h-full items-center gap-3 px-3 sm:px-4 lg:gap-4" data-header-layout="top">
          <div className="flex-shrink-0">{logoLink}</div>
          <TopNavBar className="hidden flex-1 lg:block" />
          <div className="ml-auto flex flex-shrink-0 items-center gap-0.5 sm:gap-2">{rightCluster}</div>
        </div>
      ) : (
      <div className="h-full px-4 flex items-center relative">
        <div className="flex items-center gap-2 absolute left-4">
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-white/60 backdrop-blur-sm text-gray-600"
            onClick={handleToggleSidebar}
            title={toggleLabel}
            aria-label={toggleLabel}
            data-sidebar-toggle={isDesktop ? mode : 'drawer'}
          >
            {isSidebarWide ? (
              <PanelLeftClose className="h-5 w-5" />
            ) : (
              <PanelLeft className="h-5 w-5" />
            )}
          </Button>
        </div>

        <div className={isAuthenticated ? 'absolute left-14 lg:left-1/2 lg:-translate-x-1/2' : 'absolute left-1/2 -translate-x-1/2'}>
          {logoLink}
        </div>

        <div className="absolute right-3 flex items-center gap-0.5 sm:right-4 sm:gap-2">
          {rightCluster}
        </div>
      </div>
      )}
      <LoginDialog
        open={loginDialogOpen}
        onOpenChange={(next) => {
          setLoginDialogOpen(next)
          if (!next) setLoginIntent(null)
        }}
        intent={loginIntent}
      />
      <SignupDialog open={signupDialogOpen} onOpenChange={setSignupDialogOpen} />
    </header>
  )
}